import { useListEnvironments, type PlainEnvironment } from "@/queries/environment-queries";
import { useEffect, useMemo, useState } from "react";
import { useListProjects, type PlainProject } from "@/queries/project-queries";
import { getRepoUrl } from "@/repo-url";
import { EnvironmentPhase } from "gitpod-next-api/gitpod/v1/environment_pb";
import { useListAgentExecutions } from "@/queries/agent-queries";

export type EnvironmentsGroup = {
    environments: PlainEnvironment[];
    repoFullName?: string;
    projectId?: string;
    project?: PlainProject;
    // Project data is loaded, but referenced project is missing
    projectMissing?: boolean;
};

export type UseEnvironmentsByProjectsOptions = {
    includeAgentCreatedEnvironments?: boolean;
};

export function useEnvironmentsByProjects(options: UseEnvironmentsByProjectsOptions) {
    const { includeAgentCreatedEnvironments = false } = options;

    const { data: environmentsData, isLoading: isLoadingWs, isPending: isPendingWs } = useListEnvironments();
    const { data: projectsData, isLoading: isLoadingRunners } = useListProjects();
    const { data: agentExecutionsData } = useListAgentExecutions();

    const [initialized, setInitialized] = useState<boolean>(false);
    const [data, setData] = useState<EnvironmentsGroup[]>([]);

    const environmentsCreatedByAgents = useMemo(() => {
        const createdByAgents = new Set<string>();
        for (const execution of agentExecutionsData?.agentExecutions ?? []) {
            for (const envUsage of execution.status?.usedEnvironments ?? []) {
                if (envUsage.createdByAgent && envUsage.environmentId) {
                    createdByAgents.add(envUsage.environmentId);
                }
            }
        }
        return createdByAgents;
    }, [agentExecutionsData]);

    useEffect(() => {
        const environments = environmentsData?.environments;
        if (environments === undefined) {
            return;
        }

        const projectsMap = projectsData && new Map(projectsData?.projects.map((project) => [project.id, project]));
        const map = new Map<string, EnvironmentsGroup>();

        for (const environment of environments) {
            const projectId = environment.metadata?.projectId;
            const repoUrl = getRepoUrl(environment);

            if (!includeAgentCreatedEnvironments && environmentsCreatedByAgents.has(environment.id)) {
                continue;
            }

            if (projectId) {
                const entry = map.get(projectId) || { projectId, environments: [] };
                if (projectsMap) {
                    const project = projectsMap.get(projectId);
                    if (project) {
                        entry.project = project;
                    } else {
                        entry.projectMissing = true;
                    }
                }
                entry.environments.push(environment);
                map.set(projectId, entry);
            } else if (repoUrl) {
                const repoFullName = `${repoUrl.account}/${repoUrl.repo}`;
                const entry = map.get(repoFullName) || { repoFullName, environments: [] };
                entry.environments.push(environment);
                map.set(repoFullName, entry);
            } else {
                const noRepository = "No remote repository";
                const entry = map.get(noRepository) || { repoFullName: noRepository, environments: [] };
                entry.environments.push(environment);
                map.set(noRepository, entry);
            }
        }

        const data = Array.from(map.values()).sort(orderById);
        setData(data);
        setInitialized(true);
    }, [environmentsCreatedByAgents, environmentsData?.environments, includeAgentCreatedEnvironments, projectsData]);

    // Filter out environments that are being deleted or marked for deletion
    // This ensures they disappear from the UI immediately
    const filteredGroups = useMemo(
        () =>
            data
                ?.map((group) => ({
                    ...group,
                    environments: group.environments.filter(
                        (env) =>
                            env.spec?.desiredPhase !== EnvironmentPhase.DELETED &&
                            env.status?.phase !== EnvironmentPhase.DELETING,
                    ),
                }))
                .filter((group) => group.environments.length > 0) ?? [],
        [data],
    );

    return {
        isLoading: !initialized || isPendingWs || isLoadingWs || isLoadingRunners,
        data: filteredGroups,
    };
}

function orderById(a: EnvironmentsGroup, b: EnvironmentsGroup): number {
    const toString = (g: EnvironmentsGroup) =>
        `${g.projectId || "x-no-project"}-${!g.projectId ? g.repoFullName : "is-project"}`;

    return toString(a).localeCompare(toString(b));
}
